import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { TrashSVG } from 'common/assets';
import { EnumList } from 'common/constants/enum';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import HOCInput from 'components/common/hoc-input';
import { Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { useFormState } from 'components/elements/form';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import VisaSelectInput from 'legacy-modules/master/visa-document/components/visa-select-input';
import {
  CardSection,
  HalfContainer,
  SectionContainer as RawSectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

interface Props {
  name: string;
  type: 'Insurance' | 'Visa' | 'other' | 'Vaccines';
  group: 'booking' | 'guest';
  onRemove: () => void;
  fileType: UploadFileTypeEnum;
}

export default function DocumentCard(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { setValue } = useFormContext();

  const onAfterChangeIssueDate = () => {
    setValue(`${props.name}.expiredAt`, null, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const visaType = (
    <VisaSelectInput
      name={`${props.name}.visaDocumentId`}
      label={t('common:type')}
      placeholder={t('common:type')}
    />
  );

  const insuranceType = (
    <EnumSelectInput
      name={`${props.name}.type`}
      enumType={EnumList.InsuranceType}
      label={t('common:type')}
      placeholder={t('common:type')}
    />
  );

  const documentNumber = (
    <Input
      type="text"
      name={`${props.name}.number`}
      label={t('common:document_number')}
      placeholder={t('common:document_number')}
    />
  );

  const documentName = (
    <Input
      type="text"
      label={t('common:document_name')}
      placeholder={t('common:document_name')}
      name={`${props.name}.name`}
    />
  );

  const description = (
    <Input
      type="text"
      label={t('common:description')}
      placeholder={t('common:description')}
      name={`${props.name}.description`}
    />
  );

  const issuedAt = (
    <Input
      type="date-time"
      name={`${props.name}.issuedAt`}
      label={t('common:date_of_issue')}
      placeholder={t('common:date_of_issue')}
      onAfterChange={onAfterChangeIssueDate}
    />
  );

  const expiredAt = (
    <HOCInput keys={[`${props.name}.issuedAt`]}>
      {(value) => (
        <Input
          type="date-time"
          name={`${props.name}.expiredAt`}
          label={t('common:date_of_expiry')}
          placeholder={t('common:date_of_expiry')}
          minDate={value[`${props.name}.issuedAt`] ?? undefined}
          disabled={!value[`${props.name}.issuedAt`]}
        />
      )}
    </HOCInput>
  );

  const removeButton = editable && (
    <Button
      buttonType="secondary"
      startEnhancer={(color, size) => (
        <TrashSVG width={size} height={size} color={stylesColors.redDefault} />
      )}
      isError
      onClick={props.onRemove}
    >
      {t('common:remove')}
    </Button>
  );

  const isOther = props.type === 'other';
  const isVisa = props.type === 'Visa';

  return (
    <CardSection style={{ marginBottom: 16 }}>
      {!isOther ? (
        <SectionContainer>
          <HalfContainer>{isVisa ? visaType : insuranceType}</HalfContainer>
          <HalfContainer>{documentNumber}</HalfContainer>
        </SectionContainer>
      ) : (
        <SectionContainer>
          <HalfContainer>{documentName}</HalfContainer>
          <HalfContainer>{description}</HalfContainer>
        </SectionContainer>
      )}
      <SectionContainer>
        {!isOther && (
          <HalfContainer
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <HalfContainer style={styles.fixedHalfContainer}>
              {issuedAt}
            </HalfContainer>
            <HalfContainer style={styles.fixedHalfContainer}>
              {expiredAt}
            </HalfContainer>
          </HalfContainer>
        )}
        <HalfContainer>
          <Input
            name={`${props.name}.files`}
            label={t('common:upload_document')}
            type="files"
            valueKey="file"
            previewKey="url"
            required
            fileType={props.fileType}
          />
        </HalfContainer>
      </SectionContainer>
      {removeButton}
    </CardSection>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});

const styles: { [key: string]: React.CSSProperties } = {
  fixedHalfContainer: {
    width: '47%',
  },
};
